// ====================================================================
// OUTFITS — Chocky's costume catalog and equip/unequip logic
// ====================================================================
// Design plan §6, cheapest two attachment tiers only:
//   - body "texture costumes" are MATERIAL TINTS (chockyMat.color/emissive),
//     not texture repaints. setFace() swaps chockyMat.map between expression
//     canvases (and faces/*.png overrides can replace them at any time), so
//     baking a tint into the canvases would miss every swap. A material tint
//     multiplies whatever map is active: every expression and override stays
//     tinted, the navy face ink survives multiplication, and the face contract
//     (artist-kit/README.md) is untouched because we never repaint pixels.
//   - head items are procedural meshes merged to ONE draw call, parented to
//     the Head bone via an anchor group that counters the rig's odd bone frame
//     and scale, so the hat rides the head through every animation.
//
// Slots, not combinations (design plan §6): body + head, independently.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { makeGLTFLoader } from '../core/gltf.js';
import { ULTRA_LOW_SHADOWS } from '../core/tier.js';
import { loadSave, patchSave } from './save.js';

// Items with a `quest` string are never sold: the store shows the hint until
// a quest (game/quest.js) adds them to the save's `owned` list.
export const CATALOG = [
  { id: 'cream',     slot: 'body', name: 'Classic Cream',     price: 0,  tint: 0xffffff, swatch: '#f9e8d4' },
  { id: 'mint',      slot: 'body', name: 'Minty Marshmallow', price: 10, tint: 0xa9e8c6, swatch: '#a9e8c6' },
  { id: 'berry',     slot: 'body', name: 'Strawberry Milk',   price: 10, tint: 0xf6b9d2, swatch: '#f6b9d2' },
  { id: 'blueberry', slot: 'body', name: 'Blueberry Frost',   price: 10, tint: 0xbac8f0, swatch: '#bac8f0' },
  { id: 'lemon',     slot: 'body', name: 'Lemon Sorbet',      price: 10, tint: 0xf2e8a0, swatch: '#f2e8a0' },
  { id: 'lavender',  slot: 'body', name: 'Lavender Dream',    price: 10, tint: 0xd9c8f0, swatch: '#d9c8f0' },
  // Oasis / explorer body tints (the Grove zone, src/world/zones/grove.js).
  { id: 'cactus',    slot: 'body', name: 'Cactus Green',      price: 10, tint: 0x9bc88a, swatch: '#9bc88a' },
  { id: 'sandy',     slot: 'body', name: 'Sandy Dune',        price: 10, tint: 0xe6cda0, swatch: '#e6cda0' },
  { id: 'sunset',    slot: 'body', name: 'Oasis Sunset',      price: 10, tint: 0xf6a96b, swatch: '#f6a96b' },
  // Modelled costume (Alex's wizard.glb). A "body" item with `model`/`pieces`
  // attaches real geometry to the rig instead of tinting the body — the robe
  // rides the torso and a bell sleeve rides each upper arm. Wears alongside the
  // Wizard Hat below, but each is its own slot so they mix with anything else.
  { id: 'wizardrobe', slot: 'body', name: 'Wizard Robe',      price: 30, emoji: '🧙',
    model: 'wizard', pieces: ['wizard_robe', 'wizard_sleeve_l', 'wizard_sleeve_r'] },
  { id: 'party',     slot: 'head', name: 'Party Hat',         price: 15, emoji: '🎉' },
  { id: 'wizard',    slot: 'head', name: 'Wizard Hat',        price: 20, emoji: '🧙',
    model: 'wizard', pieces: ['wizard_hat'] },
  { id: 'propeller', slot: 'head', name: 'Propeller Cap',     price: 25, emoji: '🚁' },
  { id: 'crown',     slot: 'head', name: 'Royal Crown',       price: 40, emoji: '👑' },
  // Oasis / explorer hats — sold in Chocky's Closet alongside the Grove zone.
  { id: 'visor',     slot: 'head', name: 'Sun Visor',         price: 15, emoji: '🧢' },
  { id: 'safari',    slot: 'head', name: 'Safari Hat',        price: 20, emoji: '👒' },
  { id: 'palm',      slot: 'head', name: 'Palm-Frond Crown',  price: 30, emoji: '🌴' },
  { id: 'straw',     slot: 'head', name: 'Lucky Straw Hat',   price: 0,  emoji: '👒',
    quest: 'Help Wizard Zip in the west meadow!' },
  { id: 'pith',      slot: 'head', name: 'Explorer Pith Helmet', price: 0, emoji: '🪖',
    quest: 'Find the Grove Keeper\'s lost pet out in the oasis!' },
  { id: 'bunnyears', slot: 'head', name: 'Bunny Ears',          price: 0, emoji: '🐰',
    quest: 'Round up the escaped bunnies all across Chockytown!' },
  { id: 'mountaineer', slot: 'head', name: 'Mountaineer Cap',   price: 0, emoji: '🧗',
    quest: 'Reach the summit of Obby Peak!' },
];

export function getItem(id) { return CATALOG.find(it => it.id === id) || null; }

function normalizeOutfitSave(save) {
  const validIds = new Set(CATALOG.map((it) => it.id));
  const validOwned = [...new Set(['cream', ...(save.owned || []).filter((id) => validIds.has(id))])];
  const ownedIds = new Set(validOwned);
  const body = ownedIds.has(save.equipped?.body) && getItem(save.equipped.body)?.slot === 'body'
    ? save.equipped.body
    : 'cream';
  const head = ownedIds.has(save.equipped?.head) && getItem(save.equipped.head)?.slot === 'head'
    ? save.equipped.head
    : null;
  const changed = validOwned.length !== save.owned.length
    || validOwned.some((id, i) => id !== save.owned[i])
    || body !== save.equipped.body
    || head !== save.equipped.head;
  if (changed) patchSave({ owned: validOwned, equipped: { body, head } });
  save.owned = validOwned;
  save.equipped = { body, head };
  return save;
}


// Where the hat sits: world units above the HEAD BONE's origin. Derived from
// the bone (not an absolute height) because the GLB's bind-pose bounding box
// is taller than the rendered pose — absolute "crown" heights land in the air.
// Tuned visually; hats sink down so the brim hugs the rounded head rather than
// hovering above it.
const CROWN_ABOVE_HEAD = 0.12;

let getRefs = null;        // () => { chocky, chockyMat, bones } (nulls until GLB lands)
let equipped = { body: 'cream', head: null };
let hatGroup = null;       // currently worn hat (anchor group under the Head bone)
// Head-anchor alignment, computed ONCE at onChockyReady while the model is
// guaranteed to be in rest pose at the world origin. The rig's local bone
// frames are bizarre (see the world-axis helpers in main.js), so the anchor's
// local transform is derived from world space: world-align with the model
// root, counter the rig scale, and sit at the crown point.
let anchorPos = null, anchorQuat = null, anchorScale = 1;

// ---- modelled outfits (GLB pieces pinned to joints) -----------------------
// Catalog items with a `model` carry their geometry in an external .glb (built
// on chocky.glb, one node per piece — see artist-kit/README.md "Outfits").
// Each piece rides a single joint as a rigid object: hat→Head, robe→Spine01,
// sleeves→upper arms. We pin a piece by recording, ONCE at bind pose, the joint
// world transform and the model root's world transform, then parenting the
// mesh under the joint with the bind-time offset baked into its local matrix —
// exactly how the procedural hat rides the Head bone, but per delivered piece.
const PIECE_JOINT = {
  wizard_hat:      'Head',
  wizard_robe:     'Spine01',
  wizard_sleeve_l: 'L_Upperarm',
  wizard_sleeve_r: 'R_Upperarm',
};
let bindRootWorld = null;            // chocky.matrixWorld at rest (baseScale)
const bindJointWorld = {};           // jointName -> Matrix4 at rest pose
const modelCache = new Map();        // model name -> Promise<{ pieceName -> {geometry, material, world} }>
let gltfLoader = null;
const wornPieces = { head: [], body: [] };   // attached Mesh[] per slot
const pieceGen = { head: 0, body: 0 };        // bumped on every (un)equip to drop stale async loads

export function initOutfits(refsGetter) {
  getRefs = refsGetter;
  const save = normalizeOutfitSave(loadSave());
  equipped = { ...save.equipped };
}

// Called from the GLB load callback (rest pose, model grounded at the origin).
export function onChockyReady() {
  const { chocky, bones } = getRefs();
  const head = bones && bones.Head;
  if (chocky && head) {
    chocky.updateMatrixWorld(true);
    const headQ = head.getWorldQuaternion(new THREE.Quaternion());
    const rootQ = chocky.getWorldQuaternion(new THREE.Quaternion());
    anchorQuat = headQ.invert().multiply(rootQ);
    const ws = head.getWorldScale(new THREE.Vector3());
    anchorScale = 1 / (ws.y || 1);
    const crown = head.getWorldPosition(new THREE.Vector3());
    crown.y += CROWN_ABOVE_HEAD;
    anchorPos = head.worldToLocal(crown);
    // Snapshot the rest-pose transforms modelled outfits pin against. Captured
    // here (rest pose, before any animation) so the bind-time offset is correct
    // no matter how late the outfit .glb finishes loading.
    bindRootWorld = chocky.matrixWorld.clone();
    for (const jointName of new Set(Object.values(PIECE_JOINT))) {
      const j = bones[jointName];
      if (j) bindJointWorld[jointName] = j.matrixWorld.clone();
    }
  }
  // Re-apply whatever the save says (equips made before load were recorded).
  applyBody(equipped.body);
  applyHead(equipped.head);
}

export function getEquipped() { return { ...equipped }; }

// Equip an item into its slot (id = null clears the slot; body falls back to
// the classic look). Records + persists even before the GLB has loaded.
export function equip(slot, id) {
  const item = id ? getItem(id) : null;
  if (id && (!item || item.slot !== slot)) return false;
  equipped[slot] = slot === 'body' ? (id || 'cream') : id;
  patchSave({ equipped: { ...equipped } });
  if (slot === 'body') applyBody(equipped.body);
  else if (slot === 'head') applyHead(equipped.head);
  return true;
}

function applyBody(id) {
  const { chockyMat } = getRefs();
  const item = getItem(id) || getItem('cream');
  if (chockyMat) {                         // (null until the GLB lands — re-applied later)
    // A modelled costume covers the body, so the body itself stays neutral
    // cream (white tint); a plain tint costume multiplies its colour as before.
    const tint = item.tint != null ? item.tint : 0xffffff;
    chockyMat.color.setHex(tint);
    // Keep the emissive "lift" (see CHOCKY_EMISSIVE in main.js) tinted to match,
    // so shaded undersides take the costume colour too.
    chockyMat.emissive.setHex(tint);
  }
  applyModelPieces('body', item.model ? item : null);
}

function applyHead(id) {
  removeHatGroup();
  const item = id ? getItem(id) : null;
  applyModelPieces('head', item && item.model ? item : null);
  if (!item || item.model) return;         // slot cleared, or a modelled hat handled above
  // Procedural hat: an anchor group under the Head bone (see CROWN_ABOVE_HEAD).
  const { bones } = getRefs();
  const head = bones && bones.Head;
  if (!head || !anchorPos) return;         // GLB not loaded yet — applied later
  hatGroup = new THREE.Group();
  hatGroup.position.copy(anchorPos);
  hatGroup.quaternion.copy(anchorQuat);
  hatGroup.scale.setScalar(anchorScale);
  hatGroup.add(buildHatMesh(id));
  head.add(hatGroup);
}

function removeHatGroup() {
  if (!hatGroup) return;
  hatGroup.removeFromParent();
  hatGroup.traverse(o => {
    if (o.isMesh) { o.geometry.dispose(); o.material.dispose(); }
  });
  hatGroup = null;
}

// ---- modelled outfit loading & attachment ---------------------------------

// Load an outfit .glb once and cache its pieces (geometry + material + the
// piece's authored world matrix, which sits in chocky.glb's model space).
function loadOutfitModel(name) {
  if (modelCache.has(name)) return modelCache.get(name);
  gltfLoader = gltfLoader || makeGLTFLoader();
  const p = new Promise((resolve, reject) => {
    gltfLoader.load(`${name}.glb`, (gltf) => {
      gltf.scene.updateMatrixWorld(true);
      const pieces = {};
      gltf.scene.traverse((o) => {
        if (!o.isMesh) return;
        if (o.material && o.material.map) o.material.map.colorSpace = THREE.SRGBColorSpace;
        // Register under the mesh's own name AND each named ancestor:
        // meshopt-packed GLBs (gltfpack) re-parent each mesh as an anonymous
        // child (auto-named mesh_N by GLTFLoader) under the named node that
        // carries the dequantization transform. matrixWorld folds that
        // transform in, so placement is unaffected. First registration wins.
        const entry = { geometry: o.geometry, material: o.material, world: o.matrixWorld.clone() };
        for (let n = o; n && n !== gltf.scene; n = n.parent) {
          if (n.name && !pieces[n.name]) pieces[n.name] = entry;
        }
      });
      resolve(pieces);
    }, undefined, reject);
  }).catch((err) => {
    // Do not permanently cache failed network/parse attempts: re-equipping (or
    // a later retry after assets are fixed) should be able to recover visibly.
    modelCache.delete(name);
    throw err;
  });
  modelCache.set(name, p);
  return p;
}

// Pin a piece rigidly under its joint. `piece.world` is the authored matrix in
// model space; baking jointBind⁻¹ · rootBind · world into the mesh's local
// matrix places it exactly where it was authored at bind pose, after which it
// rides the joint every frame. The game's auto-scale cancels out of the bind
// ratio, so this is correct however the model was scaled to stand 1.8m tall.
function attachPiece(piece, jointName) {
  const { bones } = getRefs();
  const joint = bones && bones[jointName];
  if (!joint || !bindJointWorld[jointName] || !bindRootWorld) return null;
  const local = new THREE.Matrix4()
    .copy(bindJointWorld[jointName]).invert()
    .multiply(bindRootWorld)
    .multiply(piece.world);
  const mesh = new THREE.Mesh(piece.geometry, piece.material);
  mesh.matrixAutoUpdate = false;
  mesh.matrix.copy(local);
  mesh.castShadow = !ULTRA_LOW_SHADOWS;
  mesh.receiveShadow = false;
  joint.add(mesh);
  return mesh;
}

// Swap the modelled pieces worn in a slot. Async (the .glb streams in), so a
// generation token drops a load whose slot changed while it was in flight.
// Cached geometry/material are NOT disposed on removal — they're reused on the
// next wear (one wizard model for the whole session).
function warnModelPieces(item, reason, extra = {}) {
  const requested = item?.pieces || [];
  const available = extra.available || [];
  console.warn(`outfit ${item?.model || '(unknown)'} (${item?.id || 'unknown'}): ${reason}`, {
    requested,
    available,
    ...extra,
  });
}

function applyProceduralHeadFallback(id) {
  if (hatGroup || id !== 'wizard') return;
  const { bones } = getRefs();
  const head = bones && bones.Head;
  if (!head || !anchorPos) return;
  hatGroup = new THREE.Group();
  hatGroup.position.copy(anchorPos);
  hatGroup.quaternion.copy(anchorQuat);
  hatGroup.scale.setScalar(anchorScale);
  hatGroup.add(buildHatMesh(id));
  head.add(hatGroup);
}

function handleModelPiecesIncomplete(slot, item, attachedCount, details) {
  const failedAll = attachedCount === 0;
  if (slot === 'body' && item.id === 'wizardrobe' && failedAll) {
    // Keep the saved robe equip intact, but render Chocky's base material as the
    // classic cream body instead of leaving an invisible/half-loaded costume.
    const { chockyMat } = getRefs();
    const cream = getItem('cream');
    if (chockyMat && cream?.tint != null) {
      chockyMat.color.setHex(cream.tint);
      chockyMat.emissive.setHex(cream.tint);
    }
    warnModelPieces(item, 'wizard robe attached zero pieces; rendering classic cream body until the model recovers', details);
  } else if (slot === 'head' && item.id === 'wizard' && failedAll) {
    warnModelPieces(item, 'wizard hat attached zero model pieces; using procedural wizard hat fallback', details);
    applyProceduralHeadFallback('wizard');
  } else if (details.missing.length || details.unattached.length) {
    warnModelPieces(item, 'model outfit incomplete', details);
  }
}

function applyModelPieces(slot, item) {
  const gen = ++pieceGen[slot];
  for (const m of wornPieces[slot]) m.removeFromParent();
  wornPieces[slot] = [];
  if (!item || !item.model || !bindRootWorld) return;   // nothing modelled, or rig not ready yet
  loadOutfitModel(item.model).then((pieces) => {
    if (pieceGen[slot] !== gen) return;                  // slot changed mid-load — discard
    const available = Object.keys(pieces).sort();
    const missing = [];
    const unattached = [];
    let attachedCount = 0;
    for (const pieceName of item.pieces) {
      const piece = pieces[pieceName];
      if (!piece) { missing.push(pieceName); continue; }
      const jointName = PIECE_JOINT[pieceName];
      const mesh = attachPiece(piece, jointName);
      if (mesh) {
        wornPieces[slot].push(mesh);
        attachedCount++;
      } else {
        unattached.push(pieceName);
      }
    }
    handleModelPiecesIncomplete(slot, item, attachedCount, { available, missing, unattached });
  }).catch((err) => {
    if (pieceGen[slot] !== gen) return;
    handleModelPiecesIncomplete(slot, item, 0, { available: [], missing: [...(item.pieces || [])], unattached: [], error: err });
  });
}

// Attach a modelled outfit to an ARBITRARY rest-pose rig — e.g. a Chocky-clone
// NPC (src/game/chockyNpc.js), not the player. `root` is the model root and
// `bones` maps the semantic joint names in PIECE_JOINT to that rig's bones;
// both must be at rest pose with world matrices current. Mirrors the player
// attach math (jointWorld⁻¹ · rootWorld · pieceWorld baked into a fixed local
// matrix) so each piece rides its joint. Resolves to the attached meshes.
export async function attachOutfitToRig(modelName, pieceNames, root, bones, { castShadow = true } = {}) {
  root.updateMatrixWorld(true);
  const rootWorld = root.matrixWorld.clone();
  const pieces = await loadOutfitModel(modelName);
  const out = [];
  for (const pieceName of pieceNames) {
    const piece = pieces[pieceName];
    const joint = bones[PIECE_JOINT[pieceName]];
    if (!piece || !joint) {
      if (!piece) console.warn(`outfit ${modelName}: missing piece "${pieceName}"`);
      continue;
    }
    joint.updateWorldMatrix(true, false);
    const local = new THREE.Matrix4()
      .copy(joint.matrixWorld).invert()
      .multiply(rootWorld)
      .multiply(piece.world);
    const mesh = new THREE.Mesh(piece.geometry, piece.material);
    mesh.material = piece.material.clone();
    mesh.material.polygonOffset = true;
    mesh.material.polygonOffsetFactor = -1;
    mesh.material.polygonOffsetUnits = -1;
    mesh.matrixAutoUpdate = false;
    mesh.matrix.copy(local);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = false;
    mesh.frustumCulled = false;   // rig may sit far from origin; don't risk culling
    joint.add(mesh);
    out.push(mesh);
  }
  return out;
}

// ---- procedural hat meshes ------------------------------------------------
// Each hat is several primitives merged into ONE geometry with vertex colours
// and a single cheap Lambert material: one draw call per worn hat.

function tintGeometry(geo, hex) {
  const c = new THREE.Color(hex);
  const n = geo.attributes.position.count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b; }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}

function buildHatMesh(id) {
  const parts = [];
  if (id === 'party') {
    const cone = tintGeometry(new THREE.ConeGeometry(0.24, 0.46, 12), 0xe05670);
    cone.translate(0, 0.19, 0);            // base sunk a little into the head
    const pompom = tintGeometry(new THREE.SphereGeometry(0.07, 8, 6), 0xfff0a0);
    pompom.translate(0, 0.44, 0);
    parts.push(cone, pompom);
  } else if (id === 'wizard') {
    const brim = tintGeometry(new THREE.CylinderGeometry(0.4, 0.42, 0.05, 14), 0x5a48a8);
    brim.translate(0, -0.02, 0);
    const cone = tintGeometry(new THREE.ConeGeometry(0.26, 0.58, 12), 0x4a3a8c);
    cone.translate(0, 0.27, 0);
    parts.push(brim, cone);
  } else if (id === 'propeller') {
    const dome = tintGeometry(new THREE.SphereGeometry(0.27, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), 0xe05656);
    dome.translate(0, -0.04, 0);
    const stick = tintGeometry(new THREE.CylinderGeometry(0.018, 0.018, 0.1, 6), 0x888888);
    stick.translate(0, 0.27, 0);
    const bladeA = tintGeometry(new THREE.BoxGeometry(0.36, 0.018, 0.07), 0xffd23f);
    bladeA.translate(0, 0.33, 0);
    const bladeB = tintGeometry(new THREE.BoxGeometry(0.36, 0.018, 0.07), 0x4a8ed0);
    bladeB.rotateY(Math.PI / 2);
    bladeB.translate(0, 0.35, 0);
    parts.push(dome, stick, bladeA, bladeB);
  } else if (id === 'crown') {
    const ring = tintGeometry(new THREE.CylinderGeometry(0.26, 0.28, 0.14, 10), 0xf2c14e);
    ring.translate(0, 0.05, 0);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const spike = tintGeometry(new THREE.ConeGeometry(0.05, 0.15, 6), 0xf2c14e);
      spike.translate(Math.cos(a) * 0.22, 0.18, Math.sin(a) * 0.22);
      parts.push(spike);
    }
    const jewel = tintGeometry(new THREE.SphereGeometry(0.05, 8, 6), 0xe05656);
    jewel.translate(0, 0.06, 0.27);
    parts.push(ring, jewel);
  } else if (id === 'straw') {
    const brim = tintGeometry(new THREE.CylinderGeometry(0.44, 0.48, 0.045, 14), 0xdcc278);
    const crown = tintGeometry(new THREE.CylinderGeometry(0.21, 0.26, 0.18, 12), 0xd4b86a);
    crown.translate(0, 0.1, 0);
    const band = tintGeometry(new THREE.CylinderGeometry(0.245, 0.272, 0.055, 12), 0xa84e2c);
    band.translate(0, 0.045, 0);
    parts.push(brim, crown, band);
  } else if (id === 'visor') {
    // A sun visor: a foam headband with a curved brim out front, no crown.
    const band = tintGeometry(new THREE.CylinderGeometry(0.27, 0.27, 0.1, 14, 1, true), 0x3fa67a);
    band.translate(0, 0.02, 0);
    const brim = tintGeometry(new THREE.CylinderGeometry(0.42, 0.42, 0.04, 16, 1, false, 0, Math.PI), 0x2e8a62);
    brim.translate(0, -0.01, 0.12);
    parts.push(band, brim);
  } else if (id === 'safari') {
    // A bush hat: a broad downturned brim under a soft tan dome.
    const brim = tintGeometry(new THREE.CylinderGeometry(0.46, 0.5, 0.05, 16), 0xc9b483);
    brim.translate(0, -0.01, 0);
    const dome = tintGeometry(new THREE.SphereGeometry(0.26, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), 0xd8c596);
    dome.translate(0, 0.0, 0);
    const band = tintGeometry(new THREE.CylinderGeometry(0.265, 0.285, 0.06, 12), 0x6e5a36);
    band.translate(0, 0.05, 0);
    parts.push(brim, dome, band);
  } else if (id === 'palm') {
    // A crown of palm fronds fanning out from a woven band.
    const band = tintGeometry(new THREE.CylinderGeometry(0.27, 0.29, 0.12, 12), 0x8a6a3c);
    band.translate(0, 0.02, 0);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const frond = tintGeometry(new THREE.ConeGeometry(0.07, 0.5, 5), 0x3fa650);
      frond.rotateZ(-0.5);
      frond.rotateY(a);
      frond.translate(Math.cos(a) * 0.18, 0.24, Math.sin(a) * 0.18);
      parts.push(frond);
    }
    parts.push(band);
  } else if (id === 'pith') {
    // An explorer's pith helmet: a pale domed shell with an all-round brim and
    // a thin chin-strap band — the Grove critter-rescue reward.
    const brim = tintGeometry(new THREE.CylinderGeometry(0.4, 0.42, 0.045, 16), 0xe4ddc4);
    brim.translate(0, -0.01, 0);
    const dome = tintGeometry(new THREE.SphereGeometry(0.29, 14, 9, 0, Math.PI * 2, 0, Math.PI / 2), 0xede7d2);
    dome.translate(0, 0.0, 0);
    const knob = tintGeometry(new THREE.SphereGeometry(0.045, 8, 6), 0xc7bfa0);
    knob.translate(0, 0.28, 0);
    const band = tintGeometry(new THREE.CylinderGeometry(0.295, 0.305, 0.05, 14), 0x8a7a4c);
    band.translate(0, 0.06, 0);
    parts.push(brim, dome, knob, band);
  } else if (id === 'bunnyears') {
    // A soft headband with two tall floppy ears — white with a pink inner —
    // the reward for the great bunny roundup (src/game/bunnyquest.js).
    const band = tintGeometry(new THREE.CylinderGeometry(0.27, 0.28, 0.1, 14), 0xf4f4f8);
    band.translate(0, 0.02, 0);
    for (const side of [-1, 1]) {
      const ear = tintGeometry(new THREE.CapsuleGeometry(0.055, 0.32, 4, 8), 0xf6f6fa);
      ear.rotateZ(side * 0.22);
      ear.translate(side * 0.12, 0.34, 0);
      const inner = tintGeometry(new THREE.CapsuleGeometry(0.028, 0.24, 4, 6), 0xf3a9c0);
      inner.rotateZ(side * 0.22);
      inner.translate(side * 0.12, 0.35, 0.035);
      parts.push(ear, inner);
    }
    parts.push(band);
  } else if (id === 'mountaineer') {
    // A cozy bobble beanie for the climb: a soft red dome over a folded cream
    // band, with a white pom-pom on top — the Obby Peak summit reward.
    const cap = tintGeometry(new THREE.SphereGeometry(0.28, 14, 9, 0, Math.PI * 2, 0, Math.PI / 2), 0xd6473e);
    cap.translate(0, 0.015, 0);
    const band = tintGeometry(new THREE.CylinderGeometry(0.285, 0.285, 0.085, 14), 0xede3cf);
    band.translate(0, 0.01, 0);
    const pom = tintGeometry(new THREE.SphereGeometry(0.06, 8, 6), 0xf4f4f0);
    pom.translate(0, 0.30, 0);
    parts.push(cap, band, pom);
  }
  const merged = mergeGeometries(parts);
  for (const g of parts) g.dispose();
  const mesh = new THREE.Mesh(merged, new THREE.MeshLambertMaterial({ vertexColors: true }));
  mesh.castShadow = !ULTRA_LOW_SHADOWS;
  return mesh;
}
